import { Modal, Stack, Group, Text, Badge, Code, Button, Box, Card, Timeline, ThemeIcon, Paper, Grid, Flex, CopyButton, ActionIcon, Tooltip, Tabs, Alert } from '@mantine/core';
import { IconExternalLink, IconGitBranch, IconHammer, IconRocket, IconCheck, IconX, IconClock, IconAlertCircle, IconCopy, IconList, IconTerminal, IconInfoCircle } from '@tabler/icons-react';
import { Deployment } from '../types';
import { useEffect, useRef, useState } from 'react';
import { deploymentsApi } from '../lib/api';
import { LiveLogsViewer } from './LiveLogsViewer';

interface DeploymentDetailsModalProps {
  deployment: Deployment | null;
  opened: boolean;
  onClose: () => void;
  onDeploymentUpdate?: (updatedDeployment: Deployment) => void;
}

export function DeploymentDetailsModal({ deployment, opened, onClose, onDeploymentUpdate }: DeploymentDetailsModalProps) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const deploymentRef = useRef<Deployment | null>(deployment);
  const [activeTab, setActiveTab] = useState<'timeline' | 'logs' | 'details'>('timeline');
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Update ref when deployment changes
  useEffect(() => {
    deploymentRef.current = deployment;
  }, [deployment]);

  // Check if status is terminal
  const isTerminalStatus = (status: string) => {
    return ['SUCCEEDED', 'FAILED', 'CANCELLED'].includes(status);
  };

  // Polling function
  const pollDeploymentStatus = async () => {
    if (!deploymentRef.current || !deploymentRef.current.buildId) return;

    try {
      setIsRefreshing(true);
      const response = await deploymentsApi.refreshStatus({ ids: [deploymentRef.current.id] });
      const updatedDeployments = response.data.items || [];
      const updatedDeployment = updatedDeployments.find(d => d.id === deploymentRef.current?.id);
      
      if (updatedDeployment && onDeploymentUpdate) {
        onDeploymentUpdate(updatedDeployment);
        
        // Stop polling if status becomes terminal
        if (isTerminalStatus(updatedDeployment.status)) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }
    } catch (error) {
      console.error('Failed to poll deployment status:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Start/stop polling based on modal state, deployment status, and active tab
  useEffect(() => {
    if (opened && deployment && !isTerminalStatus(deployment.status) && activeTab === 'timeline') {
      // Start polling every 10 seconds only for Timeline tab
      intervalRef.current = setInterval(pollDeploymentStatus, 10000);
    } else {
      // Stop polling when modal closes, status is terminal, or tab changes
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [opened, deployment?.status, activeTab]);

  // Reset tab on modal close
  useEffect(() => {
    if (!opened) {
      setActiveTab('timeline');
    }
  }, [opened]);

  // Immediate fetch when Timeline becomes active (and modal is open)
  useEffect(() => {
    if (opened && activeTab === 'timeline') {
      void pollDeploymentStatus();
    }
  }, [opened, activeTab]);

  if (!deployment) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'running':
        return 'blue';
      case 'failed':
        return 'red';
      case 'pending':
      default:
        return 'gray';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'running':
        return 'Running';
      case 'failed':
        return 'Failed';
      case 'pending':
      default:
        return 'Pending';
    }
  };

  const getPhaseIcon = (status: string, defaultIcon: React.ReactNode) => {
    switch (status) {
      case 'completed':
        return <IconCheck size={16} />;
      case 'failed':
        return <IconX size={16} />;
      case 'running':
        return <IconClock size={16} />;
      default:
        return defaultIcon;
    }
  };

  // Helper function to determine phase status with fallback logic
  const getPhaseStatus = (phaseName: string, phaseStatus: string | undefined) => {
    // If we have specific phase status, use it
    if (phaseStatus) return phaseStatus;
    
    // Fallback to overall deployment status
    if (deployment.status === 'FAILED') {
      return 'failed';
    } else if (deployment.status === 'SUCCEEDED') {
      return 'completed';
    } else if (deployment.status === 'RUNNING') {
      // For running deployments without phase info, assume first phase is running
      return phaseName === 'Clone Repository' ? 'running' : 'pending';
    } else {
      return 'pending';
    }
  };

  const phases = [
    {
      name: 'Clone Repository',
      description: 'Clone and checkout repository at specific commit',
      icon: <IconGitBranch size={16} />,
      status: getPhaseStatus('Clone Repository', deployment.phases?.clone),
    },
    {
      name: 'Build Image',
      description: 'Build Docker image using Kaniko and push to Artifact Registry',
      icon: <IconHammer size={16} />,
      status: getPhaseStatus('Build Image', deployment.phases?.build),
    },
    {
      name: 'Deploy Service',
      description: 'Deploy to Cloud Run service',
      icon: <IconRocket size={16} />,
      status: getPhaseStatus('Deploy Service', deployment.phases?.deploy),
    },
  ];

  const completedPhases = phases.filter(p => p.status === 'completed').length;
  const totalPhases = phases.length;
  const progressPercentage = (completedPhases / totalPhases) * 100;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group spacing="sm">
          <div>
            <Group spacing="sm" align="center">
              <Text weight={600} size="lg">{deployment.serviceName}</Text>
              <Badge 
                color={deployment.environment === 'PROD' ? 'red' : 'blue'}
                variant="light"
                size="sm"
              >
                {deployment.environment}
              </Badge>
              <Badge color={deployment.buildId?.startsWith('wisp:') ? 'grape' : 'gray'} variant="outline" size="sm">
                {deployment.buildId?.startsWith('wisp:') ? 'Wisp' : 'Cloud Run'}
              </Badge>
            </Group>
            <Text size="sm" color="dimmed">{deployment.repo}</Text>
          </div>
        </Group>
      }
      size="xl"
      styles={{
        header: { borderBottom: '1px solid #e9ecef' },
        body: { padding: 0 }
      }}
    >
        <Tabs value={activeTab} onTabChange={(val) => val && setActiveTab(val as 'timeline' | 'logs' | 'details')}>
          <Tabs.List pt="md" pr="md" pl="md" sx={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
            <Tabs.Tab value="timeline" icon={<IconList size={14} />}>
              Timeline
            </Tabs.Tab>
            <Tabs.Tab value="logs" icon={<IconTerminal size={14} />}>
              Live Logs
            </Tabs.Tab>
            <Tabs.Tab value="details" icon={<IconInfoCircle size={14} />}>
              Details
            </Tabs.Tab>
          </Tabs.List>

        <Tabs.Panel value="timeline" p="xl">
          {deployment.commitSha === 'disabled-environment' && deployment.status === 'CANCELLED' && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="orange"
              variant="light"
              mb="md"
              title="Environment Disabled"
            >
              This deployment was cancelled because the environment is disabled. Existing deployments have been deleted, the Cloud Run service has been removed, and no build process was started.
            </Alert>
          )}
          
          <Paper withBorder>
            <Box p="md" style={{ borderBottom: '1px solid #e9ecef' }}>
              <Group position="apart">
                <Text weight={600} size="sm">Deployment Timeline</Text>
                <Button variant="default" size="xs" onClick={() => pollDeploymentStatus()} loading={isRefreshing}>Refresh</Button>
              </Group>
            </Box>
            <Box p="md">
              {deployment.commitSha === 'disabled-environment' && deployment.status === 'CANCELLED' ? (
                <Stack align="center" spacing="md" py="xl">
                  <ThemeIcon color="orange" size={48} radius="xl">
                    <IconX size={24} />
                  </ThemeIcon>
                  <Text weight={600} size="lg" color="dimmed">
                    Deployment Cancelled
                  </Text>
                  <Text size="sm" color="dimmed" align="center">
                    This environment is currently disabled. The Cloud Run service has been deleted and no build process was started. To enable deployments, please enable the environment in the service settings.
                  </Text>
                </Stack>
              ) : (
                <Timeline active={completedPhases} bulletSize={24} lineWidth={2}>
                  {phases.map((phase, index) => (
                    <Timeline.Item
                      key={phase.name}
                      bullet={
                        <ThemeIcon
                          color={getStatusColor(phase.status)}
                          size={24}
                          radius="xl"
                        >
                          {getPhaseIcon(phase.status, phase.icon)}
                        </ThemeIcon>
                      }
                      title={
                        <Text weight={600} size="sm">
                          {phase.name}
                        </Text>
                      }
                    >
                      <Text color="dimmed" size="sm" mb="xs">
                        {phase.description}
                      </Text>
                      <Badge
                        color={getStatusColor(phase.status)}
                        variant={phase.status === 'pending' ? 'outline' : 'light'}
                        size="sm"
                      >
                        {getStatusText(phase.status)}
                      </Badge>
                    </Timeline.Item>
                  ))}
                </Timeline>
              )}
            </Box>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="details" p="xl">
          <Paper withBorder>
            <Box p="md" style={{ borderBottom: '1px solid #e9ecef' }}>
              <Group position="apart">
                <Text weight={600} size="sm">Technical Details</Text>
                {deployment.logsUrl && (
                  <Button
                    component="a"
                    href={deployment.logsUrl}
                    target="_blank"
                    rel="noreferrer"
                    variant="default"
                    size="xs"
                  >
                    {deployment.buildId?.startsWith('wisp:') ? 'Open in Wisp' : 'External Logs'}
                  </Button>
                )}
              </Group>
            </Box>
            <Box p="md">
              <Grid>
                {/* Left Column - Build Information */}
                <Grid.Col span={12} sm={6}>
                  <Stack spacing="lg">
                    <Card radius="md">
                      <Stack spacing="xs">
                        <Text size="xs" color="dimmed" weight={600} tt="uppercase" lts={1}>
                          Build Information
                        </Text>
                        <div>
                          <Text size="xs" color="dimmed" mb={4}>Commit SHA</Text>
                          <Group spacing="xs" align="center">
                            <Code 
                              block 
                              style={{ 
                                fontSize: '12px', 
                                flex: 1,
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                whiteSpace: 'nowrap',
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none'
                              }}
                              sx={{
                                '&::-webkit-scrollbar': {
                                  display: 'none'
                                }
                              }}
                            >
                              {deployment.commitSha}
                            </Code>
                            <CopyButton value={deployment.commitSha} timeout={2000}>
                              {({ copied, copy }) => (
                                <Tooltip label={copied ? 'Copied!' : 'Copy commit SHA'} withArrow>
                                  <ActionIcon 
                                    color={copied ? 'teal' : 'gray'} 
                                    onClick={copy}
                                    size="sm"
                                  >
                                    <IconCopy size={14} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Group>
                        </div>
                        {deployment.buildId && (
                          <div>
                            <Text size="xs" color="dimmed" mb={4}>Build ID</Text>
                            <Group spacing="xs" align="center">
                              <Code 
                                block 
                                style={{ 
                                  fontSize: '12px', 
                                  flex: 1,
                                  overflowX: 'auto',
                                  overflowY: 'hidden',
                                  whiteSpace: 'nowrap',
                                  scrollbarWidth: 'none',
                                  msOverflowStyle: 'none'
                                }}
                                sx={{
                                  '&::-webkit-scrollbar': {
                                    display: 'none'
                                  }
                                }}
                              >
                                {deployment.buildId}
                              </Code>
                              <CopyButton value={deployment.buildId} timeout={2000}>
                                {({ copied, copy }) => (
                                  <Tooltip label={copied ? 'Copied!' : 'Copy build ID'} withArrow>
                                    <ActionIcon 
                                      color={copied ? 'teal' : 'gray'} 
                                      onClick={copy}
                                      size="sm"
                                    >
                                      <IconCopy size={14} />
                                    </ActionIcon>
                                  </Tooltip>
                                )}
                              </CopyButton>
                            </Group>
                          </div>
                        )}
                        {deployment.imageDigest && (
                          <div>
                            <Text size="xs" color="dimmed" mb={4}>Image Digest</Text>
                            <Group spacing="xs" align="center">
                              <Code 
                                block 
                                style={{ 
                                  fontSize: '10px', 
                                  flex: 1,
                                  overflowX: 'auto',
                                  overflowY: 'hidden',
                                  whiteSpace: 'nowrap',
                                  scrollbarWidth: 'none',
                                  msOverflowStyle: 'none'
                                }}
                                sx={{
                                  '&::-webkit-scrollbar': {
                                    display: 'none'
                                  }
                                }}
                              >
                                {deployment.imageDigest}
                              </Code>
                              <CopyButton value={deployment.imageDigest} timeout={2000}>
                                {({ copied, copy }) => (
                                  <Tooltip label={copied ? 'Copied!' : 'Copy image digest'} withArrow>
                                    <ActionIcon 
                                      color={copied ? 'teal' : 'gray'} 
                                      onClick={copy}
                                      size="sm"
                                    >
                                      <IconCopy size={14} />
                                    </ActionIcon>
                                  </Tooltip>
                                )}
                              </CopyButton>
                            </Group>
                          </div>
                        )}
                      </Stack>
                    </Card>
                  </Stack>
                </Grid.Col>

                {/* Right Column - Timing Information */}
                <Grid.Col span={12} sm={6}>
                  <Stack spacing="lg">
                    <Card radius="md">
                      <Stack spacing="xs">
                        <Text size="xs" color="dimmed" weight={600} tt="uppercase" lts={1}>
                          Timing Information
                        </Text>
                        <Group position="apart">
                          <div>
                            <Text size="xs" color="dimmed">Started</Text>
                            <Text size="sm" weight={500}>
                              {new Date(deployment.createdAt).toLocaleString()}
                            </Text>
                          </div>
                        </Group>
                        <Group position="apart">
                          <div>
                            <Text size="xs" color="dimmed">Last Updated</Text>
                            <Text size="sm" weight={500}>
                              {new Date(deployment.updatedAt).toLocaleString()}
                            </Text>
                          </div>
                        </Group>
                        <Group position="apart">
                          <div>
                            <Text size="xs" color="dimmed">Duration</Text>
                            <Text size="sm" weight={500}>
                              {deployment.status === 'SUCCEEDED' || deployment.status === 'FAILED' 
                                ? `${Math.round((new Date(deployment.updatedAt).getTime() - new Date(deployment.createdAt).getTime()) / 1000)}s`
                                : 'In Progress...'
                              }
                            </Text>
                          </div>
                        </Group>
                      </Stack>
                    </Card>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Box>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="logs" p="xl">
          <LiveLogsViewer 
            buildId={deployment.buildId || null} 
            isActive={opened && activeTab === 'logs'}
            deploymentStatus={deployment.status}
          />
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}
